package org.zq.linkgame.ui.components

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import org.zq.linkgame.ui.model.LevelCategory
import org.zq.linkgame.ui.model.LevelPack
import org.zq.linkgame.ui.theme.*

/**
 * Level pack card for the level selection screen
 */
@Composable
fun LevelPackCard(
    levelPack: LevelPack,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    isCompact: Boolean = false
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isHovered by interactionSource.collectIsHoveredAsState()
    val isPressed by interactionSource.collectIsPressedAsState()
    
    val scale by animateFloatAsState(
        targetValue = when {
            isPressed -> 0.95f
            isHovered && levelPack.isUnlocked -> 1.05f
            else -> 1f
        }
    )
    
    val shadowElevation by animateFloatAsState(
        targetValue = if (isHovered && levelPack.isUnlocked) 10f else 4f
    )
    
    val cardWidth = if (isCompact) 180.dp else 300.dp
    val cardHeight = if (isCompact) 140.dp else 220.dp
    val cornerRadius = if (isCompact) 12.dp else 20.dp
    val iconSize = if (isCompact) 50.dp else 80.dp
    
    Box(
        modifier = modifier
            .graphicsLayer {
                scaleX = scale
                scaleY = scale
            }
            .width(cardWidth)
            .height(cardHeight)
            .shadow(
                elevation = shadowElevation.dp,
                shape = RoundedCornerShape(cornerRadius),
                ambientColor = if (isHovered && levelPack.isUnlocked) DeepGreen.copy(alpha = 0.3f) else Color.Black.copy(alpha = 0.1f),
                spotColor = if (isHovered && levelPack.isUnlocked) DeepGreen.copy(alpha = 0.3f) else Color.Black.copy(alpha = 0.1f)
            )
            .clip(RoundedCornerShape(cornerRadius))
            .background(Color.White)
            .alpha(if (levelPack.isUnlocked) 1f else 0.5f)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                enabled = levelPack.isUnlocked,
                onClick = onClick
            )
    ) {
        Column(
            modifier = Modifier.fillMaxSize()
        ) {
            // Theme image area (3/5 height)
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(3f)
                    .clip(RoundedCornerShape(topStart = cornerRadius, topEnd = cornerRadius))
                    .background(
                        brush = Brush.verticalGradient(
                            colors = getGradientColorsForCategory(levelPack.category)
                        )
                    ),
                contentAlignment = Alignment.Center
            ) {
                // Category-specific icon
                CategoryIcon(
                    category = levelPack.category,
                    modifier = Modifier.size(iconSize)
                )
            }
            
            // Info area (2/5 height)
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(2f)
                    .padding(horizontal = if (isCompact) 6.dp else 12.dp, vertical = if (isCompact) 4.dp else 6.dp),
                verticalArrangement = Arrangement.Center
            ) {
                AppText(
                    text = levelPack.name,
                    style = if (isCompact) MaterialTheme.typography.bodyMedium else MaterialTheme.typography.titleMedium,
                    color = DeepGreen
                )
                Spacer(modifier = Modifier.height(if (isCompact) 2.dp else 4.dp))
                AppText(
                    text = if (isCompact) "${levelPack.levelCount}关" else "关卡：${levelPack.levelCount}",
                    style = if (isCompact) MaterialTheme.typography.labelSmall else MaterialTheme.typography.bodyMedium,
                    color = DarkGray
                )
            }
        }
        
        // Lock icon overlay for locked packs
        if (!levelPack.isUnlocked) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                LockIcon(
                    size = if (isCompact) 28.dp else 40.dp,
                    color = DarkGray
                )
            }
        }
    }
}

/**
 * Get gradient colors based on level pack category
 */
private fun getGradientColorsForCategory(category: LevelCategory): List<Color> {
    return when (category) {
        LevelCategory.ANIMAL -> listOf(LightGreen, Green.copy(alpha = 0.3f))
        LevelCategory.FOOD -> listOf(LightYellow, OrangeRedLight.copy(alpha = 0.3f))
        LevelCategory.FAIRY_TALE -> listOf(LightBlue, Blue.copy(alpha = 0.3f))
    }
}

/**
 * Category-specific icon
 */
@Composable
private fun CategoryIcon(
    category: LevelCategory,
    modifier: Modifier = Modifier
) {
    when (category) {
        LevelCategory.ANIMAL -> AnimalCategoryIcon(modifier)
        LevelCategory.FOOD -> FoodCategoryIcon(modifier)
        LevelCategory.FAIRY_TALE -> FairyTaleCategoryIcon(modifier)
    }
}

/**
 * Animal category icon - simplified cute animal face
 */
@Composable
private fun AnimalCategoryIcon(modifier: Modifier = Modifier) {
    androidx.compose.foundation.Canvas(modifier = modifier) {
        val size = this.size.minDimension
        
        // Draw bear face
        drawCircle(
            color = Color(0xFF8B4513),
            radius = size * 0.4f,
            center = center
        )
        
        // Ears
        drawCircle(
            color = Color(0xFF8B4513),
            radius = size * 0.15f,
            center = androidx.compose.ui.geometry.Offset(size * 0.25f, size * 0.25f)
        )
        drawCircle(
            color = Color(0xFF8B4513),
            radius = size * 0.15f,
            center = androidx.compose.ui.geometry.Offset(size * 0.75f, size * 0.25f)
        )
        
        // Eyes
        drawCircle(
            color = Color.White,
            radius = size * 0.08f,
            center = androidx.compose.ui.geometry.Offset(size * 0.4f, size * 0.45f)
        )
        drawCircle(
            color = Color.White,
            radius = size * 0.08f,
            center = androidx.compose.ui.geometry.Offset(size * 0.6f, size * 0.45f)
        )
        drawCircle(
            color = Color.Black,
            radius = size * 0.04f,
            center = androidx.compose.ui.geometry.Offset(size * 0.4f, size * 0.45f)
        )
        drawCircle(
            color = Color.Black,
            radius = size * 0.04f,
            center = androidx.compose.ui.geometry.Offset(size * 0.6f, size * 0.45f)
        )
        
        // Nose
        drawCircle(
            color = Color.Black,
            radius = size * 0.06f,
            center = androidx.compose.ui.geometry.Offset(size * 0.5f, size * 0.58f)
        )
    }
}

/**
 * Food category icon - simplified cake/dessert
 */
@Composable
private fun FoodCategoryIcon(modifier: Modifier = Modifier) {
    androidx.compose.foundation.Canvas(modifier = modifier) {
        val size = this.size.minDimension
        
        // Cake base
        drawRoundRect(
            color = Color(0xFFFFE4B5),
            topLeft = androidx.compose.ui.geometry.Offset(size * 0.2f, size * 0.45f),
            size = androidx.compose.ui.geometry.Size(size * 0.6f, size * 0.35f),
            cornerRadius = androidx.compose.ui.geometry.CornerRadius(8f)
        )
        
        // Frosting
        drawRoundRect(
            color = Color(0xFFFFB6C1),
            topLeft = androidx.compose.ui.geometry.Offset(size * 0.15f, size * 0.35f),
            size = androidx.compose.ui.geometry.Size(size * 0.7f, size * 0.15f),
            cornerRadius = androidx.compose.ui.geometry.CornerRadius(8f)
        )
        
        // Cherry on top
        drawCircle(
            color = Color(0xFFDC143C),
            radius = size * 0.08f,
            center = androidx.compose.ui.geometry.Offset(size * 0.5f, size * 0.28f)
        )
        
        // Cherry stem
        drawLine(
            color = Color(0xFF228B22),
            start = androidx.compose.ui.geometry.Offset(size * 0.5f, size * 0.28f),
            end = androidx.compose.ui.geometry.Offset(size * 0.55f, size * 0.18f),
            strokeWidth = 3f
        )
    }
}

/**
 * Fairy tale category icon - simplified castle
 */
@Composable
private fun FairyTaleCategoryIcon(modifier: Modifier = Modifier) {
    androidx.compose.foundation.Canvas(modifier = modifier) {
        val size = this.size.minDimension
        
        // Castle body
        drawRect(
            color = Color(0xFF9370DB),
            topLeft = androidx.compose.ui.geometry.Offset(size * 0.25f, size * 0.4f),
            size = androidx.compose.ui.geometry.Size(size * 0.5f, size * 0.4f)
        )
        
        // Tower left
        drawRect(
            color = Color(0xFF9370DB),
            topLeft = androidx.compose.ui.geometry.Offset(size * 0.2f, size * 0.25f),
            size = androidx.compose.ui.geometry.Size(size * 0.15f, size * 0.55f)
        )
        
        // Tower right
        drawRect(
            color = Color(0xFF9370DB),
            topLeft = androidx.compose.ui.geometry.Offset(size * 0.65f, size * 0.25f),
            size = androidx.compose.ui.geometry.Size(size * 0.15f, size * 0.55f)
        )
        
        // Tower tops (triangles)
        val leftPath = androidx.compose.ui.graphics.Path().apply {
            moveTo(size * 0.275f, size * 0.25f)
            lineTo(size * 0.35f, size * 0.15f)
            lineTo(size * 0.2f, size * 0.25f)
            close()
        }
        drawPath(leftPath, Color(0xFF4B0082))
        
        val rightPath = androidx.compose.ui.graphics.Path().apply {
            moveTo(size * 0.725f, size * 0.25f)
            lineTo(size * 0.8f, size * 0.15f)
            lineTo(size * 0.65f, size * 0.25f)
            close()
        }
        drawPath(rightPath, Color(0xFF4B0082))
        
        // Door
        drawRoundRect(
            color = Color(0xFF8B4513),
            topLeft = androidx.compose.ui.geometry.Offset(size * 0.42f, size * 0.6f),
            size = androidx.compose.ui.geometry.Size(size * 0.16f, size * 0.2f),
            cornerRadius = androidx.compose.ui.geometry.CornerRadius(size * 0.08f, size * 0.08f)
        )
        
        // Windows
        drawCircle(
            color = Color(0xFFFFD700),
            radius = size * 0.04f,
            center = androidx.compose.ui.geometry.Offset(size * 0.35f, size * 0.5f)
        )
        drawCircle(
            color = Color(0xFFFFD700),
            radius = size * 0.04f,
            center = androidx.compose.ui.geometry.Offset(size * 0.65f, size * 0.5f)
        )
    }
}

/**
 * Game tile cell for the game grid
 */
@Composable
fun GameTileCell(
    patternType: Int,
    isSelected: Boolean,
    isMatched: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    cellSize: Dp = 80.dp
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isPressed by interactionSource.collectIsPressedAsState()
    
    val scale by animateFloatAsState(
        targetValue = when {
            isSelected -> 1.05f
            isPressed -> 0.95f
            else -> 1f
        }
    )
    
    val alpha by animateFloatAsState(
        targetValue = if (isMatched) 0f else 1f
    )
    
    // Calculate icon size proportionally (72% of cell size)
    val iconSize = cellSize * 0.72f
    
    Box(
        modifier = modifier
            .graphicsLayer {
                scaleX = scale
                scaleY = scale
                this.alpha = alpha
            }
            .size(cellSize)
            .clip(RoundedCornerShape(8.dp))
            .background(Color.White)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                enabled = !isMatched,
                onClick = onClick
            ),
        contentAlignment = Alignment.Center
    ) {
        // Selection border inside white background
        Box(
            modifier = Modifier
                .fillMaxSize()
                .then(
                    if (isSelected) {
                        Modifier
                            .padding(1.dp)
                            .border(
                                width = 3.dp,
                                color = DeepBlue,
                                shape = RoundedCornerShape(0.dp)
                            )
                    } else Modifier
                ),
            contentAlignment = Alignment.Center
        ) {
            if (!isMatched) {
                GamePatternIcon(
                    patternType = patternType,
                    size = iconSize
                )
            }
        }
    }
}

/**
 * Prop item card for the game screen
 */
@Composable
fun PropItemCard(
    propName: String,
    count: Int,
    icon: @Composable () -> Unit,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isPressed by interactionSource.collectIsPressedAsState()
    val scale by animateFloatAsState(if (isPressed) 0.9f else 1f)
    
    Column(
        modifier = modifier
            .graphicsLayer {
                scaleX = scale
                scaleY = scale
            }
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                enabled = count > 0,
                onClick = onClick
            )
            .alpha(if (count > 0) 1f else 0.5f),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Box(
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = Modifier
                    .size(56.dp)
                    .clip(androidx.compose.foundation.shape.CircleShape)
                    .background(VeryLightGray),
                contentAlignment = Alignment.Center
            ) {
                icon()
            }
            
            // Count badge - outside the clip
            if (count > 0) {
                Box(
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .offset(x = 4.dp, y = (-4).dp)
                        .size(20.dp)
                        .clip(androidx.compose.foundation.shape.CircleShape)
                        .background(OrangeRed),
                    contentAlignment = Alignment.Center
                ) {
                    AppText(
                        text = count.toString(),
                        style = MaterialTheme.typography.labelSmall,
                        color = Color.White
                    )
                }
            }
        }
        
        Spacer(modifier = Modifier.height(4.dp))
        
        AppText(
            text = propName,
            style = MaterialTheme.typography.bodyMedium,
            color = DeepBlue
        )
    }
}
